/**
 * Step6Summary — récapitulatif avant matérialisation (9.T.5.v3.6, QA §7).
 *
 * QA §7 v3.6 : afficher précisément avant confirmation :
 *   - company créée ou rattachée
 *   - project créé ou rattaché
 *   - champs qui seront appliqués
 *   - doc_direction
 *   - documents liés (mention générique)
 *   - QR draft créée ou non
 *   - liste des assureurs recipients
 *   - notice "aucune communication envoyée"
 *   - checkbox `confirmed_create` obligatoire pour stratégies (b)/(c)
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { WizardData } from "./MaterializeWizard";

type Props = {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
};

/**
 * Liste exacte des valeurs canoniques qui seront écrites (QA §7 + 1e-B : afficher
 * les champs ET leurs valeurs, pas seulement un compteur). `labelKey` = suffixe de
 * clé i18n sous `step_4_extracted_data.*_label` (réutilise les libellés de l'étape 4).
 */
function listAppliedValues(data: WizardData): Array<{ labelKey: string; value: string }> {
  const proj = data.applyProjectData ?? {};
  const comp = data.applyCompanyData ?? {};
  const out: Array<{ labelKey: string; value: string }> = [];
  if (proj.insurer_codes?.length)
    out.push({ labelKey: "insurer_codes_label", value: proj.insurer_codes.join(", ") });
  if (proj.guarantee_codes?.length)
    out.push({ labelKey: "guarantee_codes_label", value: proj.guarantee_codes.join(", ") });
  if (proj.effect_date) out.push({ labelKey: "effect_date_label", value: proj.effect_date });
  if (proj.renewal_target_date)
    out.push({ labelKey: "renewal_target_date_label", value: proj.renewal_target_date });
  if (proj.deadline_date) out.push({ labelKey: "deadline_date_label", value: proj.deadline_date });
  if (comp.employee_count !== undefined)
    out.push({ labelKey: "employee_count_label", value: String(comp.employee_count) });
  return out;
}

export function Step6Summary({ data, onChange }: Props) {
  const { t } = useTranslation();

  const appliedValues = useMemo(() => listAppliedValues(data), [data]);
  const isCreateStrategy = data.strategy !== "attach_existing_project";
  const qrEnabled =
    !!data.createQuoteRequestEnabled && (data.createQuoteRequest?.target_insurers?.length ?? 0) > 0;

  return (
    <section>
      <h3 className="mb-1 text-lg font-medium text-slate-900">
        {t("intake_events.detail.materialize.wizard.step_6_summary.title")}
      </h3>
      <p className="mb-4 text-sm text-slate-600">
        {t("intake_events.detail.materialize.wizard.step_6_summary.subtitle")}
      </p>

      <dl className="space-y-3">
        <SummaryRow
          section={t("intake_events.detail.materialize.wizard.step_6_summary.company_section")}
        >
          {data.strategy === "create_company_and_project"
            ? t("intake_events.detail.materialize.wizard.step_6_summary.company_create", {
                name: data.newCompany?.legal_name ?? "—",
              })
            : t("intake_events.detail.materialize.wizard.step_6_summary.company_attach")}
        </SummaryRow>

        <SummaryRow
          section={t("intake_events.detail.materialize.wizard.step_6_summary.project_section")}
        >
          {data.strategy === "attach_existing_project"
            ? t("intake_events.detail.materialize.wizard.step_6_summary.project_attach")
            : t("intake_events.detail.materialize.wizard.step_6_summary.project_create", {
                title: data.newProject?.title ?? "—",
              })}
        </SummaryRow>

        <SummaryRow
          section={t("intake_events.detail.materialize.wizard.step_6_summary.applied_section")}
        >
          {appliedValues.length === 0 ? (
            t("intake_events.detail.materialize.wizard.step_6_summary.applied_none")
          ) : (
            <ul className="space-y-0.5">
              {appliedValues.map((a) => (
                <li key={a.labelKey}>
                  <span className="text-xs text-slate-500">
                    {t(
                      `intake_events.detail.materialize.wizard.step_4_extracted_data.${a.labelKey}`,
                    )}
                  </span>{" "}
                  <span className="font-medium text-slate-900" translate="no">
                    {a.value}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SummaryRow>

        <SummaryRow
          section={t(
            "intake_events.detail.materialize.wizard.step_6_summary.doc_direction_section",
          )}
        >
          {data.docDirection
            ? t(
                `intake_events.detail.materialize.wizard.step_1_doc_direction.options.${data.docDirection}`,
              )
            : "—"}
        </SummaryRow>

        <SummaryRow
          section={t("intake_events.detail.materialize.wizard.step_6_summary.documents_section")}
        >
          {t("intake_events.detail.materialize.wizard.step_6_summary.documents_body")}
        </SummaryRow>

        <SummaryRow
          section={t(
            "intake_events.detail.materialize.wizard.step_6_summary.quote_request_section",
          )}
        >
          {qrEnabled ? (
            <div className="space-y-1">
              {data.createQuoteRequest?.title ? (
                <div>
                  <span className="text-xs text-slate-500">
                    {t(
                      "intake_events.detail.materialize.wizard.step_6_summary.quote_request_title_label",
                    )}
                  </span>{" "}
                  <span className="font-medium text-slate-900">
                    « {data.createQuoteRequest.title} »
                  </span>
                </div>
              ) : null}
              <div>
                <span className="text-xs text-slate-500">
                  {t(
                    "intake_events.detail.materialize.wizard.step_6_summary.quote_request_status_label",
                  )}
                </span>{" "}
                <span className="font-medium text-slate-900">
                  {t(
                    "intake_events.detail.materialize.wizard.step_6_summary.quote_request_status_draft",
                  )}
                </span>
              </div>
              <div>
                <span className="text-xs text-slate-500">
                  {t(
                    "intake_events.detail.materialize.wizard.step_6_summary.quote_request_recipients_label",
                  )}
                </span>{" "}
                <span className="text-slate-900">
                  {(data.createQuoteRequest?.target_insurers ?? []).join(", ") || "—"}
                </span>
              </div>
              <div>
                <span className="text-xs text-slate-500">
                  {t(
                    "intake_events.detail.materialize.wizard.step_6_summary.quote_request_communication_label",
                  )}
                </span>{" "}
                <span className="text-slate-700">
                  {t(
                    "intake_events.detail.materialize.wizard.step_6_summary.quote_request_communication_value",
                  )}
                </span>
              </div>
            </div>
          ) : (
            t("intake_events.detail.materialize.wizard.step_6_summary.quote_request_none")
          )}
        </SummaryRow>
      </dl>

      <p className="mt-4 rounded-md bg-slate-50 p-3 text-xs italic text-slate-600">
        {t("intake_events.detail.materialize.wizard.step_6_summary.no_emails_notice")}
      </p>

      {isCreateStrategy && (
        <label className="focus-within:ring-brand-500 mt-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 focus-within:ring-2">
          <input
            type="checkbox"
            checked={!!data.confirmedCreate}
            onChange={(e) => onChange({ confirmedCreate: e.target.checked })}
            className="focus-visible:ring-brand-500 mt-1 focus-visible:ring-2"
          />
          <span className="text-sm text-amber-900">
            {t("intake_events.detail.materialize.wizard.step_6_summary.confirmed_create_label")}
          </span>
        </label>
      )}
    </section>
  );
}

function SummaryRow({ section, children }: { section: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{section}</dt>
      <dd className="mt-0.5 text-sm text-slate-800">{children}</dd>
    </div>
  );
}
